import { create } from "zustand"
import { API, NexusSuccessResponse } from "../api/API"
import { LookupDataType, LookupTableId, LookupTableResponse } from "../models/LookupTable"
import { TableColumn } from "../models/Table"

export interface EditableLookupDataType extends LookupDataType {
    isEdited?: boolean
}

interface LookupTableStoreState {
    isFetching: boolean
    isSubmitting: boolean
    selectedTableId: LookupTableId
    primaryKey?: keyof LookupDataType
    setSelectedTableId: (selectedTableId: LookupTableId) => void
    error?: string
    rows: EditableLookupDataType[]
    columns: TableColumn<LookupDataType>[]
    updateRows: (allRows: LookupDataType[], rowsChanged: LookupDataType[]) => void
    fetchLookupTable: (tableId: LookupTableId) => Promise<void>
    updateLookupTable: () => Promise<void>
    hasBeenEdited: boolean
    reset: () => void
}

export const useLookupTableStore = create<LookupTableStoreState>((set, get) => ({
    rows: [],
    columns: [],
    isFetching: false,
    isSubmitting: false,
    selectedTableId: LookupTableId.platform,
    setSelectedTableId: (selectedTableId) => set({ selectedTableId }),
    hasBeenEdited: false,
    updateRows: (rows, rowsChanged) => {
        const { primaryKey } = get()
        if (primaryKey == null) {
            throw new Error("Primary key must exist to update rows.")
        }
        const allRowsUpdated = rows.slice()
        allRowsUpdated.forEach((row) => {
            if (row[primaryKey] === -1) {
                // Row was freshly added
                row.isEdited = true
                row[primaryKey] = undefined
            } else if (rowsChanged.some((changedRow) => changedRow[primaryKey] === row[primaryKey])) {
                // Row was updated
                row.isEdited = true
            }
        })

        set({ hasBeenEdited: rowsChanged.length > 0, rows: allRowsUpdated })
    },
    fetchLookupTable: async (tableId) => {
        set({ isFetching: true })
        try {
            const result: NexusSuccessResponse<LookupTableResponse> = await API.get(
                `/lookup-tables/${tableId}`,
                "Error fetching lookup table data",
            )

            const rows = result.data.rows
            const columns = result.data.columns

            columns.forEach((col) => {
                if (col.variant === "autocomplete") {
                    const options = [...new Set(rows.map((row) => row[col.id]))]
                    col.options = options.sort((a, b) => (a && b && a > b ? 1 : -1)) as string[]
                }
            })

            const primaryKey = result.data.columns?.find((column) => column.primary === true)?.id
            if (primaryKey == null) {
                throw new Error("Row primary key must exist in columns.")
            }

            set({ rows, columns, hasBeenEdited: false, isFetching: false, primaryKey, error: undefined })
        } catch (e) {
            set({ error: `Error fetching lookup table ${tableId}. Error was: ${e.message}`, isFetching: false })
        }
    },
    updateLookupTable: async () => {
        set({ isSubmitting: true })
        const state = get()
        const updatedRows = state.rows.filter((row) => row.isEdited === true)
        try {
            await API.put(
                `/lookup-tables/${state.selectedTableId}`,
                { rows: updatedRows.map((row) => ({ ...row, isEdited: undefined })) },
                "Failed to update table data.",
            )
            await state.fetchLookupTable(state.selectedTableId)
            set({ isSubmitting: false })
        } catch (e) {
            set({
                error: `Error updating lookup table ${state.selectedTableId}. Error was: ${e.message}`,
                isSubmitting: false,
            })
        }
    },
    reset: () => set({ rows: [], columns: [], hasBeenEdited: false, primaryKey: undefined, error: undefined }),
}))
